import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import UserProfileWithCompany from "@/shared/components/ui/Avatar/UserProfileWithCompany";
import MetricItem from "@/features/profiling/components/Profiling/MetricItem";
import BorderCard from "../components/BorderCard";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CustomCheckbox from "@/shared/components/ui/Checkbox/CustomCheckbox";
import AIPill from "@/shared/components/ui/AiPill/AIPill";
import CardTitle from "../components/CardTitle";
import SequenceNewsCard from "./SequenceNewsCard";
import SearchablePopoverSelect from "@/shared/components/ui/Select/SearchablePopoverSelect";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import { sanitizeCompanyLogoUrl } from "@/features/dashboard/utils/accountsDashboardUtils";
function addRowIds(items, prefix) {
  return (items || []).map((t, idx) => ({
    ...t,
    __rowId: `${prefix}-${idx}-${t.priority ?? ""}-${String(t.signal_theme ?? "").slice(0, 48)}`,
  }));
}

function sortByPriority(arr) {
  return [...arr].sort((a, b) => (a.priority || 999) - (b.priority || 999));
}

function getSignalRelevanceScore(signal) {
  return Math.round(
    (signal?.news || []).reduce(
      (sum, item) => sum + (Number(item?.news_score) || 0),
      0,
    ),
  );
}

function mapSignals(signals) {
  return sortByPriority(
    addRowIds(
      (signals || []).map((signal) => ({
        ...signal,
        relevance_score: getSignalRelevanceScore(signal),
        news: signal.news || [],
        positioning: signal.positioning || "",
      })),
      "signal",
    ),
  );
}

/** Same sort as the news list UI — numeric keys in selectedNews are sorted indices. */
function sortNewsByScore(news) {
  return [...(news || [])].sort((a, b) => {
    const aScore = typeof a === "object" ? (a.news_score ?? 0) : 0;
    const bScore = typeof b === "object" ? (b.news_score ?? 0) : 0;
    return bScore - aScore;
  });
}



function extractNewsTextForApi(newsItem) {
  if (typeof newsItem === "string") return newsItem;
  return (
    newsItem?.article_summary ??
    newsItem?.content ??
    newsItem?.text ??
    newsItem?.title ??
    newsItem?.summary ??
    newsItem?.body ??
    ""
  );
}

const IndividualSequenceTrigger = ({
  currentLead,
  individualSequencing,
  leads,
  currentLeadIndex,
  isLoadingIndividualSequencing,
  isAiResearchOff = false,
  contentGeneration,
  isUpdating = false,
  isContentGenerated = false,
  onNextLead,
  onPreviousLead,
  onSelectLead,
  onUpdate,
}) => {
  const campaignId = useCampaignId();
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [selectedSignals, setSelectedSignals] = useState(new Set());
  const hasLocalSelection = useRef(false);

  const signals = useMemo(() => {
    if (!individualSequencing?.signals?.length) return [];
    return mapSignals(individualSequencing.signals);
  }, [individualSequencing]);

  const leadOptions = useMemo(
    () =>
      (leads || []).map((lead, index) => {
        const title = lead.full_name || "N/A";
        const description =
          [lead.designation, lead.company].filter(Boolean).join(" · ") || "—";
        return {
          key: String(index),
          title,
          description,
          subtitleParts: [lead.designation || "N/A", lead.company || "N/A"],
          searchText: [
            lead.full_name,
            lead.designation,
            lead.company,
            lead.email,
          ]
            .filter(Boolean)
            .join(" "),
        };
      }),
    [leads],
  );

  const handleLeadSelect = (key) => {
    onSelectLead?.(Number(key));
  };

  useEffect(() => {
    setExpandedCards(new Set());
    setSelectedSignals(new Set());
    hasLocalSelection.current = false;
  }, [currentLead?.contact_id]);

  useEffect(() => {
    if (isLoadingIndividualSequencing || signals.length === 0) return;

    setSelectedSignals((prev) => {
      if (hasLocalSelection.current && prev.size > 0) return prev;
      const selectedSignal =
        signals.find((signal) => signal.selected) ||
        signals.find((signal) => signal.priority === 1) || signals[0];
      return new Set([selectedSignal.__rowId]);
    });
  }, [signals, currentLead?.contact_id, isLoadingIndividualSequencing]);

  const toggleCard = (cardId) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const toggleSignal = (signalId, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const signal = signals.find((t) => String(t.__rowId) === String(signalId));
    if (!signal?.news?.length) return;

    hasLocalSelection.current = true;
    setSelectedSignals((prev) => {
      const newSet = new Set();
      if (!prev.has(signalId)) {
        newSet.add(signalId);
      }
      return newSet;
    });
  };

  const handleUpdate = () => {
    const prospectId = currentLead?.contact_id ?? currentLead?.id;
    if (!campaignId || !prospectId || selectedSignals.size === 0) {
      return;
    }

    const selectedSignalId = Array.from(selectedSignals)[0];
    const signal = signals.find((t) => String(t.__rowId) === String(selectedSignalId));
    if (!signal || !signal.news?.length) return;

    const newsContentArray = sortNewsByScore(signal.news)
      .map(extractNewsTextForApi)
      .filter(Boolean);

    if (newsContentArray.length === 0) return;

    onUpdate({
      campaignId,
      prospectId,
      newsContent: newsContentArray,
      selectedSignalTrigger: signal.trigger,
      selectedSignalTheme: signal.signal_theme,
    });
  };

  const renderSignalCard = (signal) => {
    const signalId = signal.__rowId;
    const isExpanded = expandedCards.has(signalId);
    const isSignalSelected = selectedSignals.has(signalId);
    const signalName = signal.signal_theme || "";
    // const relevanceScore =
    //   signal.relevance_score || getSignalRelevanceScore(signal);
    const isBestFit = signal.priority === 1;
    const sortedNews = sortNewsByScore(signal.news);

    return (
      <div className="flex flex-col w-full relative" key={signalId}>
        <div
          className="p-4 flex justify-between items-center cursor-pointer relative overflow-hidden transition-all duration-300 ease-in-out border-b border-gray-200 dark:border-gray-800 w-full"
          onClick={() => toggleCard(signalId)}
        >
          <div className="flex flex-col gap-1.5 w-full">
            {signal?.trigger && (
              <p className="text-[10.5px] font-medium text-secondary ml-8">
                {signal?.trigger?.toUpperCase()}
              </p>
            )}
            <div className="flex justify-between w-full items-center gap-2">
              <div className="flex gap-4 items-center min-w-0">
                <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                  <CustomCheckbox
                    checked={isSignalSelected}
                    onChange={(e) => toggleSignal(signalId, e)}
                  />
                </div>
                <h5 className="text-xs font-medium text-gray-800 dark:text-gray-100 line-clamp-2">
                  {signalName}
                </h5>
              </div>
              <div className="flex gap-2 items-center shrink-0">
                <ChevronDown
                  size={20}
                  color="#717680"
                  className={`cursor-pointer transition-transform duration-300 ease-in-out ${
                    isExpanded ? "rotate-180" : "rotate-0"
                  }`}
                />
              </div>
            </div>
            {/* signal score and ai pill */}
            <div className="flex gap-2  items-end justify-end">
              {/* {relevanceScore > 0 && (
                <div className="flex gap-2 items-center pl-7">
                  <span className="text-[10.5px] text-gray-700 font-medium px-2 py-0.5 rounded-full bg-gray-100">
                    {relevanceScore}% Relevance
                  </span>
                </div>
              )} */}

              {isBestFit && (
                <AIPill
                  text={isContentGenerated ? "AI Suggested" : "Best Fit"}
                />
              )}
            </div>
          </div>
        </div>

        <div
          className="grid transition-[grid-template-rows,opacity] duration-400 ease-in-out"
          style={{
            gridTemplateRows: isExpanded ? "1fr" : "0fr",
            opacity: isExpanded ? 1 : 0,
          }}
        >
          <div className="overflow-hidden">
            <div
              className={`flex flex-col transform transition-all duration-300 ease-out ${
                isExpanded ? "translate-y-0" : "-translate-y-4"
              } ${isExpanded ? "border-b border-gray-200 dark:border-gray-800" : ""}`}
            >
              <div className="px-4 pt-4 -mb-2">
                <h6 className="text-[10px] font-medium text-gray-900 dark:text-gray-200 uppercase tracking-wide">
                  News Sources
                </h6>
              </div>

              {sortedNews.map((newsItem, index) => {
                const newsId =
                  typeof newsItem === "string" ? index : newsItem.id || index;
                const newsKey = `${signalId}-${newsId}`;

                const isLastNews = index === sortedNews.length - 1;

                return (
                  <div
                    key={newsKey}
                    className={`transform transition-all duration-300 ease-out ${
                      isExpanded
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }`}
                    style={{
                      transitionDelay: isExpanded ? `${index * 50}ms` : "0ms",
                    }}
                  >
                    <SequenceNewsCard
                      news={newsItem}
                      expanded={isExpanded}
                      showNewsScore={false}
                      hideBottomBorder={isLastNews && !!signal.positioning}
                    />
                  </div>
                );
              })}

              {/* {signal.positioning && (
                <>
                  <div className="border-t-1.5 border-dashed border-gray-300" />
                  <div className="p-4 bg-secondary-transparent">
                    <h6 className="text-[10.5px] font-medium text-secondary uppercase tracking-wide mb-2">
                      Positioning
                    </h6>
                    <p className="text-xs font-normal text-gray-800 leading-relaxed">
                      {signal.positioning}
                    </p>
                  </div>
                </>
              )} */}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 w-[34%] h-full">
      {/* User profile card */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border-[0.5px] border-gray-200 dark:border-gray-800 p-2.5 flex flex-col gap-2 shrink-0">
        <div className="flex flex-col gap-1.5 w-full">
          <div className="flex items-center gap-2.5 w-full">
            <div className="shrink-0">
              <UserProfileWithCompany
                name={currentLead?.full_name || "N/A"}
                designation={currentLead?.designation || "N/A"}
                UserImg={currentLead?.avatarUrl || ""}
                companyLogo={sanitizeCompanyLogoUrl(currentLead?.companyLogo) || ""}
                companyName={currentLead?.company || "N/A"}
              />
            </div>
            <div className="flex flex-col w-full min-w-0 gap-[7px] relative">
              <div className="flex w-full items-center">
                <SearchablePopoverSelect
                  options={leadOptions}
                  selectedKey={String(currentLeadIndex)}
                  onSelect={handleLeadSelect}
                  searchPlaceholder="Search leads..."
                  emptyMessage="No leads found"
                  className="relative min-w-0 flex-1 pr-16"
                />
                <div className="absolute right-0 top-1/2 -translate-y-1/2">
                  {leads.length > 0 && (
                    <div className="flex gap-1.5 items-center text-gray-500">
                      <button
                        onClick={onPreviousLead}
                        disabled={currentLeadIndex === 0}
                        className={`cursor-pointer ${
                          currentLeadIndex === 0
                            ? "opacity-30 cursor-not-allowed"
                            : "hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                        {currentLeadIndex + 1}/{leads.length}
                      </span>
                      <button
                        onClick={onNextLead}
                        disabled={currentLeadIndex === leads.length - 1}
                        className={`cursor-pointer ${
                          currentLeadIndex === leads.length - 1
                            ? "opacity-30 cursor-not-allowed"
                            : "hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border-[0.5px] border-gray-100 dark:border-gray-800 w-full" />
          {currentLead && (
            <div className="flex items-center text-[10.5px] w-full justify-between">
              <MetricItem
                title="Buying Authority"
                value={individualSequencing?.buying_authority?.value || "—"}
                reason={individualSequencing?.buying_authority?.reason || "—"}
                bgcolor="bg-green-50 dark:bg-emerald-950/40"
                txtcolor="text-green-600 dark:text-emerald-400"
                isLoading={isLoadingIndividualSequencing}
              />
              <div className="w-[1.2px] h-full bg-[#EFF0F4] dark:bg-gray-800" />
              <MetricItem
                title="Buying Intent"
                value={individualSequencing?.buying_intent?.value || "N/A"}
                reason={individualSequencing?.buying_intent?.reason || "—"}
                bgcolor="bg-gray-100 dark:bg-gray-800"
                txtcolor="text-gray-600 dark:text-gray-300"
                isLoading={isLoadingIndividualSequencing}
              />
              <div className="w-[1.5px] h-full bg-[#EFF0F4] dark:bg-gray-800" />
              <MetricItem
                title="Sales Score"
                value={individualSequencing?.sales_score?.value || "N/A"}
                reason={individualSequencing?.sales_score?.reason || "—"}
                bgcolor="bg-gray-100 dark:bg-gray-800"
                txtcolor="text-gray-600 dark:text-gray-300"
                isLoading={isLoadingIndividualSequencing}
              />
            </div>
          )}
        </div>
      </div>

      {/* Signals section */}
      <div className="flex flex-col flex-1 min-h-0 bg-white dark:bg-gray-900 rounded-2xl">
        <BorderCard className="shrink-0">
          <CardTitle text="Signals" />
          <SecondaryButton
            title={isUpdating ? "Updating" : "Update"}
            onClick={handleUpdate}
            disabled={isUpdating || selectedSignals?.size === 0}
            isLoading={isUpdating}
          />
        </BorderCard>
        {isLoadingIndividualSequencing ? (
          <BorderCard
            variant="bottom"
            padding="py-8 px-4"
            className="flex-1 flex items-center justify-center"
          >
            <Loader text="Loading signals..." />
          </BorderCard>
        ) : isAiResearchOff ? (
          <BorderCard
            variant="bottom"
            padding="py-8 px-4"
            className="flex-1 flex items-center justify-center"
          >
            <p className="text-sm text-gray-500 text-center">
              {contentGeneration === "off"
                ? "AI is off for this campaign, so no research runs for this lead."
                : "Every step in this campaign sends a fixed message, so no AI research runs for this lead."}
            </p>
          </BorderCard>
        ) : signals.length === 0 ? (
          <BorderCard
            variant="bottom"
            padding="py-8 px-4"
            className="flex-1 flex items-center justify-center"
          >
            <p className="text-sm text-gray-500">No signals available</p>
          </BorderCard>
        ) : (
          <BorderCard
            variant="bottom"
            padding="py-0 px-0"
            className="flex-1 overflow-y-auto flex flex-col scrollbar-hide"
          >
            {signals.map((signal) => renderSignalCard(signal))}
          </BorderCard>
        )}
      </div>
    </div>
  );
};

export default IndividualSequenceTrigger;
